export const testStrategyModule = {
  id: "test-strategy-enterprise",
  title: "Enterprise Testing Strategy & Planning",
  description: "Develop comprehensive testing strategies, test planning methodologies, and quality assurance frameworks for enterprise applications.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Design comprehensive testing strategies for enterprise applications
- Create effective test plans with clear coverage metrics and quality gates
- Implement risk-based testing approaches for complex systems
- Establish quality assurance processes that scale with enterprise development

## Prerequisites
- Software development experience
- Basic understanding of testing concepts
- Knowledge of software development lifecycle (SDLC)

## Overview
Enterprise testing strategy failures cost organizations an average of $1.7M annually in defects reaching production. This module teaches systematic approaches to testing that prevent critical issues and ensure product quality at scale.

---

## Key Concepts

### Testing Strategy Framework

#### Test Pyramid Architecture
**Unit Tests (70%)**: Fast, isolated tests for individual components
**Integration Tests (20%)**: Verify system component interactions
**End-to-End Tests (10%)**: Full user workflow validation

#### Risk-Based Testing
**Risk Assessment**: Identify high-impact, high-probability failure areas
**Test Prioritization**: Focus testing effort on critical business functions
**Coverage Optimization**: Maximize defect detection with limited resources

### Test Planning Methodologies

#### Test-Driven Development (TDD)
**Red-Green-Refactor Cycle**: Write failing test, implement code, refactor
**Specification by Example**: Tests serve as living documentation
**Design Benefits**: Better code architecture through testability focus

#### Behavior-Driven Development (BDD)
**Gherkin Syntax**: Given-When-Then scenarios for business requirements
**Stakeholder Collaboration**: Non-technical stakeholders can understand tests
**Acceptance Criteria**: Clear definition of done for features

### Quality Assurance Processes

#### Test Environment Management
**Environment Consistency**: Maintain parity across dev, staging, production
**Data Management**: Test data creation, maintenance, and cleanup
**Configuration Management**: Environment-specific settings and secrets

#### Defect Management
**Defect Lifecycle**: Discovery, triage, assignment, resolution, verification
**Severity Classification**: Critical, High, Medium, Low impact levels
**Root Cause Analysis**: Prevent similar defects through process improvement

---

## Practical Examples

### Example 1: E-commerce Testing Strategy
Comprehensive testing approach for enterprise e-commerce platform.

**Critical User Journeys**:
1. User registration and authentication
2. Product search and browsing
3. Shopping cart and checkout process
4. Payment processing and order confirmation
5. Order tracking and customer service

**Risk Assessment Matrix**:
- High Risk: Payment processing, user authentication, inventory management
- Medium Risk: Search functionality, product recommendations
- Low Risk: UI styling, non-critical notifications

**Testing Approach**:
- Unit tests for business logic (payment calculations, inventory updates)
- Integration tests for external service connections (payment gateways, shipping APIs)
- End-to-end tests for critical user paths (purchase flow)

### Example 2: Microservices Testing Strategy
Testing approach for distributed enterprise microservices architecture.

**Service-Level Testing**:
- Unit tests for individual service logic
- Component tests for service APIs
- Contract tests between service boundaries

**System-Level Testing**:
- Integration tests for service orchestration
- End-to-end tests for business workflows
- Chaos engineering for resilience testing

### Example 3: Legacy System Modernization Testing
Testing strategy during gradual legacy system replacement.

**Parallel Testing**: Run new and legacy systems simultaneously
**Shadow Testing**: Route production traffic to new system without affecting users
**Canary Testing**: Gradually roll out new system to subset of users
**Rollback Planning**: Automated fallback to legacy system if issues detected

---

## Hands-On Lab

### Lab: Comprehensive Test Strategy Design
Design and implement a testing strategy for an enterprise CRM system.

**System Requirements**:
- Customer data management with 100K+ records
- Sales pipeline tracking and reporting
- Integration with email marketing and accounting systems
- Mobile and web interfaces
- Multi-tenant architecture for different business units

**Deliverables**:
1. Risk assessment matrix with identified critical areas
2. Test pyramid allocation with coverage targets
3. Test environment strategy and data management plan
4. Quality gates and acceptance criteria definition
5. Defect management process and escalation procedures

**Quality Metrics**:
- Code coverage targets by test type
- Defect detection rates by testing phase
- Mean time to resolution for different severity levels
- Customer satisfaction scores and production incident rates

---

## Assessment

### Testing Strategy Checklist
- [ ] Risk assessment identifies critical system areas
- [ ] Test pyramid balances speed, reliability, and coverage
- [ ] Test environments mirror production configuration
- [ ] Quality gates prevent defective code deployment
- [ ] Defect management process includes root cause analysis
- [ ] Test coverage metrics align with business risk
- [ ] Testing strategy scales with development team growth
- [ ] Stakeholder communication includes non-technical participants

### Strategic Considerations
**Budget Allocation**: Balance automation investment with manual testing needs
**Tool Selection**: Choose tools that integrate well with existing development stack
**Team Skills**: Plan training and knowledge transfer for testing practices
**Maintenance**: Factor in ongoing test maintenance costs and technical debt

This module establishes the foundation for effective enterprise testing that prevents defects, reduces costs, and accelerates delivery.
`,
  duration: 60,
  codeExamples: [
    {
      title: "Risk-Based Test Planning Matrix",
      language: "javascript",
      code: `// Risk assessment and test planning framework
class TestPlanningFramework {
  constructor() {
    this.riskMatrix = new Map();
    this.testAllocation = {
      unit: 0.7,
      integration: 0.2,
      e2e: 0.1
    };
  }

  assessRisk(feature, criteria) {
    const businessImpact = this.calculateBusinessImpact(criteria);
    const technicalComplexity = this.calculateTechnicalComplexity(criteria);
    const changeFrequency = this.calculateChangeFrequency(criteria);
    
    const riskScore = (businessImpact * 0.4) + 
                     (technicalComplexity * 0.3) + 
                     (changeFrequency * 0.3);
    
    const riskLevel = this.categorizeRisk(riskScore);
    
    this.riskMatrix.set(feature, {
      score: riskScore,
      level: riskLevel,
      businessImpact,
      technicalComplexity,
      changeFrequency,
      testingPriority: this.calculateTestingPriority(riskLevel)
    });
    
    return this.riskMatrix.get(feature);
  }

  calculateBusinessImpact(criteria) {
    const factors = {
      revenue: criteria.revenueImpact || 1, // 1-5 scale
      users: criteria.userImpact || 1,      // 1-5 scale  
      compliance: criteria.complianceRisk || 1, // 1-5 scale
      reputation: criteria.reputationRisk || 1  // 1-5 scale
    };
    
    return (factors.revenue * 0.3) + 
           (factors.users * 0.3) + 
           (factors.compliance * 0.2) + 
           (factors.reputation * 0.2);
  }

  generateTestPlan(features) {
    const testPlan = {
      summary: {
        totalFeatures: features.length,
        highRiskFeatures: 0,
        estimatedEffort: 0
      },
      testAllocation: {},
      riskMitigation: []
    };

    features.forEach(feature => {
      const risk = this.riskMatrix.get(feature.name);
      
      if (risk.level === 'HIGH') {
        testPlan.summary.highRiskFeatures++;
        testPlan.riskMitigation.push({
          feature: feature.name,
          mitigationStrategy: this.generateMitigationStrategy(risk),
          additionalTesting: this.getAdditionalTestingRequirements(risk)
        });
      }

      testPlan.testAllocation[feature.name] = this.allocateTestingEffort(risk);
      testPlan.summary.estimatedEffort += testPlan.testAllocation[feature.name].totalEffort;
    });

    return testPlan;
  }

  allocateTestingEffort(risk) {
    const baseEffort = 40; // hours
    const riskMultiplier = {
      'LOW': 0.5,
      'MEDIUM': 1.0,
      'HIGH': 2.0,
      'CRITICAL': 3.0
    };

    const totalEffort = baseEffort * riskMultiplier[risk.level];
    
    return {
      totalEffort,
      unit: Math.round(totalEffort * this.testAllocation.unit),
      integration: Math.round(totalEffort * this.testAllocation.integration),
      e2e: Math.round(totalEffort * this.testAllocation.e2e),
      manual: this.calculateManualTestingEffort(risk),
      automation: this.calculateAutomationEffort(risk)
    };
  }
}`
    },
    {
      title: "Quality Gates Implementation",
      language: "javascript",
      code: `// Quality gates for enterprise development pipeline
class QualityGatesManager {
  constructor() {
    this.gates = {
      commit: new CommitQualityGate(),
      build: new BuildQualityGate(),
      deploy: new DeploymentQualityGate(),
      release: new ReleaseQualityGate()
    };
  }

  async evaluateGate(stage, metrics) {
    const gate = this.gates[stage];
    if (!gate) {
      throw new Error('Unknown quality gate: ' + stage);
    }

    const result = await gate.evaluate(metrics);
    
    if (!result.passed) {
      await this.handleGateFailure(stage, result);
      throw new QualityGateFailure(stage, result.failures);
    }

    return result;
  }

  async handleGateFailure(stage, result) {
    // Log failure details
    console.error('Quality gate failure at stage: ' + stage, result.failures);
    
    // Notify relevant teams
    await this.notificationService.sendAlert({
      type: 'QUALITY_GATE_FAILURE',
      stage: stage,
      failures: result.failures,
      buildId: result.buildId,
      timestamp: new Date().toISOString()
    });

    // Block deployment if critical failure
    if (result.severity === 'CRITICAL') {
      await this.deploymentService.blockDeployment(result.buildId);
    }
  }
}

class BuildQualityGate {
  constructor() {
    this.criteria = {
      unitTestCoverage: { min: 80, weight: 0.3 },
      integrationTestCoverage: { min: 70, weight: 0.2 },
      codeQuality: { maxComplexity: 10, weight: 0.2 },
      securityScan: { maxCritical: 0, maxHigh: 2, weight: 0.3 }
    };
  }

  async evaluate(metrics) {
    const results = {
      passed: true,
      score: 0,
      failures: [],
      buildId: metrics.buildId
    };

    // Unit test coverage check
    if (metrics.unitTestCoverage < this.criteria.unitTestCoverage.min) {
      results.passed = false;
      results.failures.push({
        criterion: 'Unit Test Coverage',
        expected: this.criteria.unitTestCoverage.min + '%',
        actual: metrics.unitTestCoverage + '%',
        severity: 'HIGH'
      });
    }

    // Integration test coverage check  
    if (metrics.integrationTestCoverage < this.criteria.integrationTestCoverage.min) {
      results.passed = false;
      results.failures.push({
        criterion: 'Integration Test Coverage',
        expected: this.criteria.integrationTestCoverage.min + '%',
        actual: metrics.integrationTestCoverage + '%',
        severity: 'MEDIUM'
      });
    }

    // Code complexity check
    if (metrics.cyclomaticComplexity > this.criteria.codeQuality.maxComplexity) {
      results.passed = false;
      results.failures.push({
        criterion: 'Code Complexity',
        expected: '<= ' + this.criteria.codeQuality.maxComplexity,
        actual: metrics.cyclomaticComplexity,
        severity: 'MEDIUM'
      });
    }

    // Security vulnerability check
    if (metrics.security.critical > this.criteria.securityScan.maxCritical) {
      results.passed = false;
      results.failures.push({
        criterion: 'Critical Security Vulnerabilities',
        expected: '<= ' + this.criteria.securityScan.maxCritical,
        actual: metrics.security.critical,
        severity: 'CRITICAL'
      });
    }

    // Calculate overall quality score
    results.score = this.calculateQualityScore(metrics);
    
    return results;
  }

  calculateQualityScore(metrics) {
    let score = 0;
    
    // Unit test coverage contribution
    const unitScore = Math.min(metrics.unitTestCoverage / this.criteria.unitTestCoverage.min, 1);
    score += unitScore * this.criteria.unitTestCoverage.weight;
    
    // Integration test coverage contribution
    const integrationScore = Math.min(metrics.integrationTestCoverage / this.criteria.integrationTestCoverage.min, 1);
    score += integrationScore * this.criteria.integrationTestCoverage.weight;
    
    // Code quality contribution
    const complexityScore = Math.max(0, 1 - (metrics.cyclomaticComplexity / this.criteria.codeQuality.maxComplexity));
    score += complexityScore * this.criteria.codeQuality.weight;
    
    // Security contribution
    const securityScore = (metrics.security.critical === 0 && metrics.security.high <= this.criteria.securityScan.maxHigh) ? 1 : 0;
    score += securityScore * this.criteria.securityScan.weight;
    
    return Math.round(score * 100);
  }
}`
    }
  ],
  practice: {
    title: "Design Enterprise Testing Strategy",
    description: "Create a comprehensive testing strategy for a multi-tenant SaaS application with various user roles and integrations.",
    starter: `// Basic application without testing strategy
class SaasApplication {
  async createUser(userData) {
    return await database.insert('users', userData);
  }
  
  async processPayment(paymentData) {
    return await paymentService.charge(paymentData);
  }
  
  async generateReport(userId, reportType) {
    const data = await database.getUserData(userId);
    return reportGenerator.create(reportType, data);
  }
}`,
    solution: `// Comprehensive testing strategy implementation

// 1. Risk Assessment Framework
class TestingStrategy {
  constructor() {
    this.riskAssessment = {
      userManagement: { risk: 'MEDIUM', businessImpact: 3, complexity: 2 },
      paymentProcessing: { risk: 'CRITICAL', businessImpact: 5, complexity: 4 },
      reportGeneration: { risk: 'HIGH', businessImpact: 4, complexity: 3 },
      dataSync: { risk: 'HIGH', businessImpact: 4, complexity: 4 },
      authentication: { risk: 'CRITICAL', businessImpact: 5, complexity: 3 }
    };
    
    this.testAllocation = this.calculateTestAllocation();
  }

  calculateTestAllocation() {
    return {
      paymentProcessing: { unit: 60, integration: 40, e2e: 20, manual: 10 },
      authentication: { unit: 40, integration: 30, e2e: 15, manual: 8 },
      reportGeneration: { unit: 35, integration: 25, e2e: 10, manual: 5 },
      userManagement: { unit: 30, integration: 20, e2e: 8, manual: 4 },
      dataSync: { unit: 45, integration: 35, e2e: 12, manual: 6 }
    };
  }
}

// 2. Test Pyramid Implementation
describe('Payment Processing - Critical Path', () => {
  // Unit Tests (60 hours allocated)
  describe('Unit Tests', () => {
    test('should calculate tax correctly for different regions', () => {
      const calculator = new TaxCalculator();
      expect(calculator.calculate(100, 'US-CA')).toBe(108.25);
      expect(calculator.calculate(100, 'EU-DE')).toBe(119.00);
    });

    test('should validate payment data before processing', () => {
      const validator = new PaymentValidator();
      expect(() => validator.validate({ amount: -100 }))
        .toThrow('Amount must be positive');
    });
  });

  // Integration Tests (40 hours allocated)
  describe('Integration Tests', () => {
    test('should process payment through external gateway', async () => {
      const paymentService = new PaymentService();
      const result = await paymentService.processPayment({
        amount: 100,
        currency: 'USD',
        paymentMethod: 'card_test_token'
      });
      
      expect(result.status).toBe('succeeded');
      expect(result.transactionId).toBeDefined();
    });
  });

  // E2E Tests (20 hours allocated)
  describe('E2E Tests', () => {
    test('complete payment flow from cart to confirmation', async () => {
      await page.goto('/checkout');
      await page.fill('[data-testid=card-number]', '4242424242424242');
      await page.click('[data-testid=pay-button]');
      await page.waitForSelector('[data-testid=payment-success]');
      
      expect(await page.textContent('[data-testid=confirmation-number]'))
        .toMatch(/CONF-\d{6}/);
    });
  });
});

// 3. Quality Gates Configuration
const qualityGates = {
  commit: {
    unitTestCoverage: 85,
    linting: true,
    securityScan: true
  },
  build: {
    integrationTestCoverage: 75,
    performanceThresholds: {
      buildTime: 300, // 5 minutes max
      bundleSize: 500 // 500KB max
    }
  },
  staging: {
    e2eTestCoverage: 90,
    loadTesting: {
      rps: 100,
      errorRate: 0.1
    }
  },
  production: {
    smokeTesting: true,
    monitoringAlerts: true,
    rollbackPlan: true
  }
};

// 4. Test Environment Strategy
class TestEnvironmentManager {
  async setupEnvironment(type) {
    const config = this.getEnvironmentConfig(type);
    
    await this.provisionInfrastructure(config);
    await this.deployApplication(config.version);
    await this.seedTestData(config.dataSet);
    await this.configureMonitoring(config.monitoring);
    
    return this.validateEnvironment(config.healthChecks);
  }

  getEnvironmentConfig(type) {
    const configs = {
      development: {
        version: 'latest',
        dataSet: 'minimal',
        monitoring: 'basic'
      },
      staging: {
        version: 'release-candidate',
        dataSet: 'production-like',
        monitoring: 'full'
      },
      production: {
        version: 'stable',
        dataSet: 'live',
        monitoring: 'enterprise'
      }
    };
    
    return configs[type];
  }
}`
  },
  quiz: {
    question: "What percentage of tests should typically be unit tests in the enterprise testing pyramid?",
    options: [
      "40-50%",
      "60-70%",
      "70-80%", 
      "80-90%"
    ],
    correctAnswer: 2,
    explanation: "The testing pyramid recommends 70-80% unit tests because they are fast, reliable, and provide quick feedback. This allows for comprehensive coverage of business logic while keeping build times reasonable."
  }
};